import { Folder } from '@/api/generatedApi';
import { useUpdateFolderDescription } from '@/hooks/api/folders/useUpdateFolderDescription';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { TruncatedText } from '@/components/TruncatedText';
import { alpha, Box, Button, Popover, TextField, useTheme } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useState } from 'react';

export const EditableDescriptionCell = ({ params }: { params: GridRenderCellParams }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | undefined>();
  const [editValue, setEditValue] = useState('');
  const [optimisticValue, setOptimisticValue] = useState<string | undefined>();

  const { permissions } = useGetCurrentUser();
  const theme = useTheme();
  const folder = params.row as Folder;

  const serverValue = folder.description;
  const hasPendingOptimisticUpdate = optimisticValue !== undefined && optimisticValue !== serverValue;
  const currentValue = hasPendingOptimisticUpdate ? optimisticValue : serverValue;
  const displayValue = currentValue ?? '';

  const { updateDescription } = useUpdateFolderDescription({
    folder,
    onSuccess: updatedFolder => {
      setOptimisticValue(updatedFolder.description ?? '');
    },
    onError: () => {
      setOptimisticValue(undefined);
    },
  });

  const handleCellClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!permissions.canCreateFolders) return;
    setEditValue(displayValue);
    setAnchorElement(event.currentTarget);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed === displayValue) {
      handleCancel();
      return;
    }
    setOptimisticValue(trimmed);
    setIsEditing(false);
    setAnchorElement(undefined);
    updateDescription(trimmed);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setAnchorElement(undefined);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    }
    if (event.key === 'Escape') {
      handleCancel();
    }
  };

  if (!permissions.canCreateFolders) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: displayValue ? '8px' : 0,
          cursor: 'default',
        }}
      >
        {displayValue ? <TruncatedText>{displayValue}</TruncatedText> : ''}
      </Box>
    );
  }

  return (
    <>
      <Box
        data-testid={AssetsPageObjectModel.EditableDescriptionCell}
        onClick={handleCellClick}
        onPointerDown={event => event.stopPropagation()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: displayValue ? '8px' : 0,
          cursor: 'pointer',
          backgroundColor: isEditing ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
          border: isEditing ? `1px solid ${alpha(theme.palette.primary.main, 0.5)}` : '1px solid transparent',
          borderRadius: isEditing ? '4px' : '0px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isEditing ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
          },
        }}
      >
        {displayValue ? (
          <TruncatedText>{displayValue}</TruncatedText>
        ) : (
          isHovered && <span style={{ color: 'inherit', opacity: 0.5, fontStyle: 'italic' }}>Click to add...</span>
        )}
      </Box>
      <Popover
        open={isEditing}
        anchorEl={anchorElement}
        onClose={handleCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onPointerDown={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              overflow: 'visible',
              p: 2,
              width: 300,
            },
          },
        }}
      >
        <TextField
          autoFocus
          fullWidth
          multiline
          maxRows={4}
          value={editValue}
          onChange={event => setEditValue(event.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{
            htmlInput: { maxLength: 255 },
            formHelperText: { sx: { textAlign: 'right' } },
          }}
          helperText={`${editValue.length}/255`}
          size='small'
          placeholder='Enter a description...'
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button size='small' onClick={handleCancel}>
            Cancel
          </Button>
          <Button size='small' variant='contained' onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Popover>
    </>
  );
};
